import type { Leaves, SizeColumnParams } from '@talos/kyoko';

/** Inputs for toggling the primary position size column between legacy quantity and server-driven notional. */
type ServerDrivenPositionSizeColumnOptions<T extends object> = {
  /** Org flag: when false, use `legacy` field paths only. */
  enableServerDrivenNotionalAmount: boolean;
  /** Ag-grid paths for raw size + currency (quantity / instrument asset). */
  legacy: {
    field: Leaves<T>;
    currencyField: Leaves<T>;
  };
  /** Ag-grid paths for display value + ccy when the feed sends notional; plus row hint for the size column bypass. */
  server: {
    field: Leaves<T>;
    currencyField: Leaves<T>;
    /** True when the value in `field` is already notional (skip contract multiplier in the size column). */
    isValueAlreadyNotional: (row: T) => boolean;
  };
};

/**
 * Returns field + size params for the primary position column: legacy quantity/scaling when the org flag is off,
 * unified server notional fields + bypass when on.
 */
export function buildServerDrivenPositionSizeColumnFields<T extends object>(
  options: ServerDrivenPositionSizeColumnOptions<T>
): {
  field: Leaves<T>;
  params: Pick<SizeColumnParams<T>, 'currencyField' | 'isValueAlreadyNotional'>;
  /** Always the legacy quantity field + instrument currency; use for a hidden “Position (Legacy)” column when the primary uses server notional. */
  legacy: {
    field: Leaves<T>;
    params: Pick<SizeColumnParams<T>, 'currencyField'>;
  };
} {
  const legacy = {
    field: options.legacy.field,
    params: { currencyField: options.legacy.currencyField },
  };
  if (!options.enableServerDrivenNotionalAmount) {
    return {
      field: options.legacy.field,
      params: { currencyField: options.legacy.currencyField },
      legacy,
    };
  }
  return {
    field: options.server.field,
    params: {
      currencyField: options.server.currencyField,
      isValueAlreadyNotional: options.server.isValueAlreadyNotional,
    },
    legacy,
  };
}
